import { baseUrl } from '../../../shared/utils';
import {
  type Client,
  type ClientApiItem,
  type ClientDetailApiResponse,
  type CreateClientRequest,
  type CreateClientResponse,
  type ListClientsParams,
  type ListClientsResponse,
  type UpdateClientRequest,
} from '../types';

/**
 * Typed API error with HTTP status and response body.
 * Thrown by all data-source functions on non-OK responses.
 */
export class ApiError extends Error {
  status: number;

  body: unknown;

  constructor(status: number, body: unknown) {
    // Surface the API's error message when available (shape: { error: string })
    const apiMessage =
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null;

    super(apiMessage ?? `API error: ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Throws ApiError if response is not OK, otherwise returns the response */
async function assertOk(response: Response): Promise<Response> {
  if (!response.ok) {
    let body: unknown;

    try {
      body = await response.json();
    } catch {
      body = null;
    }

    throw new ApiError(response.status, body);
  }

  return response;
}

/**
 * Normalizes the detail API response, mapping snake_case `custom_claims`
 * to camelCase `customClaims`.
 */
export function normalizeClientDetail(
  response: ClientDetailApiResponse
): Client {
  return {
    id: response.clientId,
    clientId: response.clientId,
    clientName: response.clientName,
    description: response.description ?? null,
    roles: response.roles,
    customClaims: response.custom_claims ?? {},
    enabled: response.enabled,
    certificateCreatedAt: response.certificateCreatedAt,
    jwksUrl: response.jwksUrl,
  };
}

/**
 * Normalizes a list API item, mapping snake_case `custom_claims`
 * to camelCase `customClaims` and defaulting optional fields.
 */
export function normalizeClientListItem(item: ClientApiItem): Client {
  return {
    id: item.clientId,
    clientId: item.clientId,
    clientName: item.clientName,
    description: item.description ?? null,
    roles: item.roles,
    customClaims: item.custom_claims ?? {},
    enabled: item.enabled,
    certificateCreatedAt: item.certificateCreatedAt,
    jwksUrl: item.jwksUrl,
  };
}

/** Builds a JSON body for create/update requests */
function buildJsonBody(
  data: CreateClientRequest | UpdateClientRequest
): string {
  const body: Record<string, unknown> = {};

  if ('clientName' in data && data.clientName) {
    body.clientName = data.clientName;
  }

  if (data.description !== undefined) {
    body.description = data.description;
  }

  if (data.roles) {
    body.roles = data.roles;
  }

  if (data.customClaims) {
    body.customClaims = data.customClaims;
  }

  if (data.publicCert) {
    body.publicCert = data.publicCert;
  }

  if (data.jwksUrl) {
    body.jwksUrl = data.jwksUrl;
  }

  if ('enabled' in data && data.enabled !== undefined) {
    body.enabled = data.enabled;
  }

  return JSON.stringify(body);
}

/** Lists clients with pagination */
export async function listClients(
  params: ListClientsParams
): Promise<ListClientsResponse> {
  const query = new URLSearchParams();

  query.set('page', String(params.page));
  query.set('size', String(params.size));

  const response = await fetch(
    `${baseUrl.authms}/v1/clients?${query.toString()}`
  );

  await assertOk(response);

  return response.json() as Promise<ListClientsResponse>;
}

/** Creates a new client credential — always sends JSON */
export async function createClient(
  data: CreateClientRequest
): Promise<CreateClientResponse> {
  const response = await fetch(`${baseUrl.authms}/v1/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: buildJsonBody(data),
  });

  await assertOk(response);

  return response.json() as Promise<CreateClientResponse>;
}

/** Fetches a single client by ID and normalizes the response */
export async function getClientById(clientId: string): Promise<Client> {
  const response = await fetch(
    `${baseUrl.authms}/v1/clients/${encodeURIComponent(clientId)}`,
    { headers: { 'Content-Type': 'application/json' } }
  );

  await assertOk(response);

  const data = (await response.json()) as ClientDetailApiResponse;

  return normalizeClientDetail(data);
}

/** Updates an existing client — always sends JSON */
export async function updateClient(
  clientId: string,
  data: UpdateClientRequest
): Promise<Client> {
  const response = await fetch(
    `${baseUrl.authms}/v1/clients/${encodeURIComponent(clientId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: buildJsonBody(data),
    }
  );

  await assertOk(response);

  const apiResponse = (await response.json()) as ClientDetailApiResponse;

  return normalizeClientDetail(apiResponse);
}

/** Deletes a client by ID */
export async function deleteClient(clientId: string): Promise<void> {
  const response = await fetch(
    `${baseUrl.authms}/v1/clients/${encodeURIComponent(clientId)}`,
    { method: 'DELETE' }
  );

  await assertOk(response);
}
